import React, { useEffect, useState, useCallback } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './i18n';
import './App.css';
import { ToastProvider } from './components/ui/ToastProvider';
import { MessageBoxProvider } from './components/ui/MessageBox';
import { LogoProvider } from './contexts/LogoContext';

// Essential components (always needed on initial load)
import MasterTemplate from './templates/MasterTemplate';
import ScrollToTop from './components/layout/ScrollToTop';
import SignIn from './components/Auth/SignIn';
import HomePage from './components/Landing/HomePage';
import RootRoute from './components/RootRoute';
import SuspenseFallback from './components/Common/SuspenseFallback';

// Lazy-loaded route components (downloaded only when user navigates to them)
const SignUp = React.lazy(() => import('./components/Auth/SignUp'));
const SelfAssessmentSignUp = React.lazy(() => import('./components/Auth/SelfAssessmentSignUp'));
const EmailVerification = React.lazy(() => import('./components/Auth/EmailVerification'));
const SelfAssessmentOrganizationWizard = React.lazy(() => import('./components/Onboarding/SelfAssessmentOrganizationWizard'));
const SelfAssessmentDashboard = React.lazy(() => import('./components/SelfAssessment/SelfAssessmentDashboard'));
const SelfAssessmentLayout = React.lazy(() => import('./components/SelfAssessment/SelfAssessmentLayout'));
const SelfAssessmentPageLayout = React.lazy(() => import('./components/SelfAssessment/SelfAssessmentPageLayout'));
const SelfAssessmentReport = React.lazy(() => import('./components/Demo/SelfAssessmentReport'));
const Dashboard = React.lazy(() => import('./components/Landing/Dashboard'));
const LocationDashboard = React.lazy(() => import('./components/LocationTracking/LocationDashboard'));
const ProfilePage = React.lazy(() => import('./components/Profile/ProfilePage'));
const RTLTest = React.lazy(() => import('./components/layout/RTLTest'));
const AssessmentCreation = React.lazy(() => import('./components/Assessment/AssessmentCreation'));
const AssessmentDetailsPage = React.lazy(() => import('./components/Assessment/AssessmentDetailsPage'));
const AssessmentSession = React.lazy(() => import('./components/AssessmentExecution/AssessmentSession'));
const AssessmentRunner = React.lazy(() => import('./components/AssessmentRunner'));
const RecommendationsManagement = React.lazy(() => import('./components/Recommendations/RecommendationsManagement'));
const AssessmentReports = React.lazy(() => import('./components/Reports/AssessmentReports'));
const MaturityReport = React.lazy(() => import('./components/Reports/MaturityReport'));
const ImprovementRoadmap = React.lazy(() => import('./components/Reports/ImprovementRoadmap'));
const AdministrationDashboard = React.lazy(() => import('./components/Admin/AdministrationDashboard'));
const BackupPage = React.lazy(() => import('./components/Admin/BackupPage'));
const RestorePage = React.lazy(() => import('./components/Admin/RestorePage'));
const UserManagement = React.lazy(() => import('./components/Admin/UserManagement'));
const PermissionsManagement = React.lazy(() => import('./components/Admin/PermissionsManagement'));
const OrganizationManagement = React.lazy(() => import('./components/Organization/OrganizationManagement'));
const SettingsPage = React.lazy(() => import('./components/Settings/SettingsPage'));
const HelpPage = React.lazy(() => import('./components/Help/HelpPage'));
const ManualPage = React.lazy(() => import('./components/Manual/ManualPage'));
const DocumentationPage = React.lazy(() => import('./components/Documentation/DocumentationPage'));
const AIChat = React.lazy(() => import('./components/AI/AIChat'));
const AIAgentConfigPage = React.lazy(() => import('./components/Admin/AIAgentConfigPage'));
const SelfAssessmentUsersManagement = React.lazy(() => import('./components/Admin/SelfAssessmentUsersManagement'));
const SelfAssessmentActivityPage = React.lazy(() => import('./components/Admin/SelfAssessmentActivityPage'));
const LeadScoringPage = React.lazy(() => import('./components/Admin/LeadScoringPage'));
const SelfAssessmentOrganizationsPage = React.lazy(() => import('./components/Admin/SelfAssessmentOrganizationsPage'));
const SelfAssessmentRankingsPage = React.lazy(() => import('./components/Admin/SelfAssessmentRankingsPage'));
const EmailCampaignsPage = React.lazy(() => import('./components/Admin/EmailCampaignsPage'));
const SelfAssessmentConfigPage = React.lazy(() => import('./components/Admin/SelfAssessmentConfigPage'));
const OrgGroupsPage = React.lazy(() => import('./components/Admin/OrgGroupsPage'));
const PaymentMethodsPage = React.lazy(() => import('./components/Admin/PaymentMethodsPage'));
const UpgradePage = React.lazy(() => import('./components/Upgrade/UpgradePage'));
const SubscriptionManagement = React.lazy(() => import('./components/Upgrade/SubscriptionManagement'));
const InvoiceHistory = React.lazy(() => import('./components/Upgrade/InvoiceHistory'));
const PricingPage = React.lazy(() => import('./components/Pricing/PricingPage'));
const WeightConfiguration = React.lazy(() => import('./components/Assessment/WeightConfiguration'));
const TargetSetting = React.lazy(() => import('./components/Assessment/TargetSetting'));
const AssessmentCycles = React.lazy(() => import('./components/Assessment/AssessmentCycles'));
const AssessorAssignment = React.lazy(() => import('./components/Assessment/AssessorAssignment'));
const ConsensusView = React.lazy(() => import('./components/Assessment/ConsensusView'));
const EvidenceSufficiency = React.lazy(() => import('./components/Assessment/EvidenceSufficiency'));
const WorkflowActions = React.lazy(() => import('./components/Assessment/WorkflowActions'));
const AuditLog = React.lazy(() => import('./components/Admin/AuditLog'));
const SurveyResponsePage = React.lazy(() => import('./components/Survey/SurveyResponsePage'));
const InviteAcceptPage = React.lazy(() => import('./components/Invite/InviteAcceptPage'));
const SurveyDetailsPage = React.lazy(() => import('./components/Survey/SurveyDetailsPage'));
const SurveyResponsesPage = React.lazy(() => import('./components/Survey/SurveyResponsesPage'));
const SurveyResponseDetailPage = React.lazy(() => import('./components/Survey/SurveyResponseDetailPage'));
const InterviewDetailsPage = React.lazy(() => import('./components/Interview/InterviewDetailsPage'));
const InterviewResponsePage = React.lazy(() => import('./components/Interview/InterviewResponsePage'));
const FrameworkBuilderWizard = React.lazy(() => import('./components/Framework/Builder/FrameworkBuilderWizard'));
const FrameworkManagement = React.lazy(() => import('./components/Framework/FrameworkManagement'));
const FrameworkManagementPage = React.lazy(() => import('./components/Framework/Management/FrameworkManagementPage'));
const FrameworkDetailsPage = React.lazy(() => import('./components/Framework/Management/FrameworkDetailsPage'));
const FrameworkStructureView = React.lazy(() => import('./components/Framework/FrameworkStructureView'));
const StandardsSelectionDemo = React.lazy(() => import('./pages/StandardsSelectionDemo'));
const PublicVerification = React.lazy(() => import('./components/Accreditation/PublicVerification'));
const PublicRegistry = React.lazy(() => import('./components/Accreditation/PublicRegistry'));
const AccreditationAnalytics = React.lazy(() => import('./components/Accreditation/AccreditationAnalytics'));
const AuditTrailViewer = React.lazy(() => import('./components/Accreditation/AuditTrailViewer'));
const BenchmarkDashboard = React.lazy(() => import('./components/Accreditation/BenchmarkDashboard'));
const AccreditationRenewal = React.lazy(() => import('./components/Accreditation/AccreditationRenewal'));
const ComplianceReport = React.lazy(() => import('./components/Accreditation/ComplianceReport'));
const AccreditationDashboard = React.lazy(() => import('./components/Accreditation/AccreditationDashboard'));
const AccreditationApplications = React.lazy(() => import('./components/Accreditation/AccreditationApplications'));
const AccreditationApplicationDetail = React.lazy(() => import('./components/Accreditation/AccreditationApplicationDetail'));
const AccreditationCriteria = React.lazy(() => import('./components/Accreditation/AccreditationCriteria'));
const CertificateManagement = React.lazy(() => import('./components/Accreditation/CertificateManagement'));
const AccreditationSettings = React.lazy(() => import('./components/Accreditation/AccreditationSettings'));
const AccreditationProfile = React.lazy(() => import('./components/Accreditation/AccreditationProfile'));

// Services
import { authService } from './services/authService';
import { PermissionProvider } from './contexts/PermissionContext';
import { getApiBaseUrl } from './utils/apiConfig';
import { shouldUseSelfAssessmentRoutes, shouldUseSelfAssessmentRoutesSync } from './utils/userAccessHelpers';

// Route Guards
import FullAccountRoute from './components/Guards/FullAccountRoute';
import SelfAssessmentRoute from './components/Guards/SelfAssessmentRoute';
import AdminRoute from './components/Guards/AdminRoute';

const API_BASE_URL = process.env.REACT_APP_API_URL || getApiBaseUrl();

/** Redirect legacy /demo/maturity-report/:id to /self-assessment/maturity-report/:id */
function DemoMaturityReportRedirect() {
  const { assessmentId } = useParams();
  return <Navigate to={`/self-assessment/maturity-report/${assessmentId}`} replace />;
}

/** Redirect legacy /frameworks/:code/details to /frameworks/:code */
function FrameworkDetailsRedirect() {
  const { code } = useParams();
  return <Navigate to={`/frameworks/${code}`} replace />;
}

/** Redirect legacy /frameworks/builder/edit/:code to /framework/builder/edit/:code */
function FrameworkBuilderEditRedirect() {
  const { code } = useParams();
  return <Navigate to={`/framework/builder/edit/${code}`} replace />;
}

function App() {
  const { t, i18n } = useTranslation();
  // CRITICAL: Start as NOT authenticated - only set to true after verification
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  // CRITICAL: Start loading as true to show HomePage immediately
  const [loading, setLoading] = useState(true);
  const [checkingOrganization, setCheckingOrganization] = useState(false);
  const [needsOrganization, setNeedsOrganization] = useState(false);

  useEffect(() => {
    // Check if user is authenticated on app load
    const userData = localStorage.getItem('user');
    
    // IMMEDIATE CHECK: If user with package_tier is on /dashboard, redirect BEFORE anything else
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        // Use sync version for immediate check (will check if package_tier is in user object)
        if (shouldUseSelfAssessmentRoutesSync(parsedUser) && parsedUser.organization_id) {
          const currentPath = window.location.pathname;
          if (currentPath === '/dashboard' || currentPath === '/') {
            window.location.href = '/self-assessment/dashboard';
            return; // Exit immediately, don't continue loading
          }
        }
      } catch (e) {
        // Ignore parse errors, continue with normal flow
      }
    }
    
    // First validate the token format and expiration
    if (!authService.isAuthenticated()) {
      // Token is invalid, expired, or missing
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
      setLoading(false);
      return;
    }
    
    // Token format is valid, now validate with backend BEFORE setting authenticated state
    if (userData) {
      try {
        const parsedUser = JSON.parse(userData);
        
        
        // Map title code to role if not already mapped
        const titleCodeToRole = {
          'SYS_ADMIN': 'System Admin',
          'HR_MANAGER': 'HR Manager',
          'ASSESSMENT_MANAGER': 'Assessment Manager',
          'ASSESSOR': 'Assessor',
          'DEPT_HEAD': 'Department Head',
          'SENIOR_STAFF': 'Senior Staff',
          'STAFF': 'Staff',
          'INTERN': 'Intern'
        };
        
        const userWithRole = {
          ...parsedUser,
          role: parsedUser.role || titleCodeToRole[parsedUser.title_code] || 'User'
        };
        
        // Validate with backend FIRST before setting authenticated state
        authService.getCurrentUser()
          .then(async (freshUser) => {
            
            
            // Migration: Phase 8.4 - No longer use account_type, check package_tier instead
            // Also merge organization_id if needed
            if (!freshUser.organization_id && parsedUser.organization_id) {
              freshUser.organization_id = parsedUser.organization_id;
            }
            
            
            
            if (freshUser && freshUser.role) {
              // CRITICAL: Redirect users with package_tier BEFORE setting state to prevent route rendering
              const canAccessSelfAssessment = await shouldUseSelfAssessmentRoutes(freshUser);
              if (canAccessSelfAssessment && freshUser.organization_id) {
                const currentPath = window.location.pathname;
                if (currentPath === '/dashboard' || currentPath === '/') {
                  window.location.href = '/self-assessment/dashboard';
                  return; // Exit immediately, prevent state update
                }
              }
              
              setUser(freshUser);
              localStorage.setItem('user', JSON.stringify(freshUser));
              setIsAuthenticated(true);
            } else {
              // Backend returned invalid user
              throw new Error('Invalid user data from backend');
            }
          })
          .catch(error => {
            // Only clear if it's an authentication error (401, 403), not network error
            const isAuthError = error.response?.status === 401 || 
                               error.response?.status === 403 ||
                               error.message.includes('Session expired') ||
                               error.message.includes('401') ||
                               error.message.includes('Unauthorized') ||
                               error.message.includes('Invalid token');
            
            if (isAuthError) {
              localStorage.removeItem('token');
              localStorage.removeItem('user');
              setUser(null);
              setIsAuthenticated(false);
            } else {
              // Network error or other issue - use cached data but show warning
              console.warn('Using cached user data due to network/server error:', error.message);
              
              // CRITICAL: Redirect users with package_tier even with cached data
              // Use sync version for cached data check
              if (shouldUseSelfAssessmentRoutesSync(userWithRole) && userWithRole.organization_id) {
                const currentPath = window.location.pathname;
                if (currentPath === '/dashboard' || currentPath === '/') {
                  window.location.href = '/self-assessment/dashboard';
                  return; // Exit immediately
                }
              }
              
              setUser(userWithRole);
              setIsAuthenticated(true);
            }
          })
          .finally(() => {
            setLoading(false);
          });
      } catch (error) {
        console.error('Error parsing user data:', error);
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
        setIsAuthenticated(false);
        setLoading(false);
      }
    } else {
      // No user data but token exists (shouldn't happen, but handle it)
      setIsAuthenticated(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Set document direction based on language
    const isRTL = i18n.language === 'ar';
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  // CRITICAL: Prevent any redirects to /signin from root path
  useEffect(() => {
    const currentPath = window.location.pathname;
    
    // If on root path and not authenticated, ensure we stay on root
    if (currentPath === '/' && !isAuthenticated && !user && !loading) {
      // Force HomePage to show - already handled by route, but ensure no redirects
    }
    
    // Allow navigation to /signin - it's a valid route
    // The route itself will handle showing the signin page
  }, [isAuthenticated, user, loading]);

  // Monitor for any programmatic redirects to /signin
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Don't allow navigation away if we're on root and not authenticated
      if (window.location.pathname === '/' && !isAuthenticated && !user) {
        // Stay on home page
      }
    };

    // REMOVED: Interceptors that block navigation to /signin
    // Users should be able to navigate to /signin explicitly via buttons/links
    // The route itself will handle showing the signin page
  }, [isAuthenticated, user, loading]);

  // CRITICAL: Monitor pathname changes and redirect demo users away from /dashboard
  useEffect(() => {
    if (!isAuthenticated) return;

    const currentPath = window.location.pathname;
    
    // Check user state first
    if (user) {
      // Use sync version for immediate check
      if ((currentPath === '/dashboard' || currentPath === '/') && shouldUseSelfAssessmentRoutesSync(user) && user.organization_id) {
        window.location.href = '/self-assessment/dashboard';
        return;
      }
    }

    // Also check localStorage as fallback (works even if user state not set yet)
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        // Use sync version for localStorage check
        const shouldUseSelfAssessment = shouldUseSelfAssessmentRoutesSync(parsedUser);
        if (shouldUseSelfAssessment && parsedUser.organization_id && (currentPath === '/dashboard' || currentPath === '/')) {
          window.location.href = '/self-assessment/dashboard';
          return;
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
  }, [isAuthenticated, user]);

  // Monitor pathname changes using interval (since we can't use useLocation outside Router)
  useEffect(() => {
    if (!isAuthenticated) return;

    const checkPath = () => {
      const currentPath = window.location.pathname;
      try {
        const storedUser = localStorage.getItem('user');
        if (storedUser) {
          const parsedUser = JSON.parse(storedUser);
          // Use sync version for interval check
          const shouldUseSelfAssessment = shouldUseSelfAssessmentRoutesSync(parsedUser);
          if (shouldUseSelfAssessment && parsedUser.organization_id && (currentPath === '/dashboard' || currentPath === '/')) {
            window.location.href = '/self-assessment/dashboard';
          }
        }
      } catch (e) {
        // Ignore
      }
    };

    // 5s is sufficient — this only redirects self-assessment users landing on /dashboard.
    // A tighter interval (e.g. 500ms) wastes CPU; route changes are rare after login.
    const interval = setInterval(checkPath, 5000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  // Function to check if user with package_tier needs organization
  const checkOrganizationRequirement = useCallback(async (userData) => {
    // Check if user should use self-assessment routes (has package_tier)
    const canAccess = await shouldUseSelfAssessmentRoutes(userData);
    if (!canAccess) {
      return false; // Not a user with package_tier, no check needed
    }
    
    // Quick check: if user already has organization_id, no wizard needed
    if (userData.organization_id) {
      return false; // Has organization, no wizard needed
    }
    
    // Verify with backend
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return false;
      }

      const response = await fetch(`${API_BASE_URL}/self-assessment/organization/check`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        return !data.has_organization; // Return true if needs organization
      }
    } catch (error) {
      console.error('Error checking organization:', error);
      // On error, don't block user - let them proceed
      return false;
    }
    
    return false;
  }, []);

  // Check organization requirement after user is authenticated
  useEffect(() => {
    const checkOrg = async () => {
      if (isAuthenticated && user) {
        const currentPath = window.location.pathname;
        
        // Skip if already on correct pages
        if (currentPath === '/onboarding/organization' || 
            currentPath === '/self-assessment/dashboard' ||
            currentPath.startsWith('/assessment/run')) {
          return;
        }

        // IMMEDIATE redirect for users with package_tier on wrong page
        // Use async version since we're in useEffect
        const canAccess = await shouldUseSelfAssessmentRoutes(user);
        if (canAccess && user.organization_id) {
          if (currentPath === '/dashboard' || currentPath === '/') {
            window.location.href = '/self-assessment/dashboard';
            return; // Exit early, don't check organization requirement
          }
        }

        setCheckingOrganization(true);
        const needsOrg = await checkOrganizationRequirement(user);
        setNeedsOrganization(needsOrg);
        setCheckingOrganization(false);

        if (needsOrg) {
          // Redirect to wizard - use window.location to force full page reload
          // This ensures the route protection works correctly
          window.location.href = '/onboarding/organization';
        } else {
          // Check if user has package_tier and redirect if needed
          const canAccess = await shouldUseSelfAssessmentRoutes(user);
          if (canAccess && user.organization_id) {
            // Force redirect users with package_tier away from /dashboard (fallback)
            if (currentPath === '/dashboard' || currentPath === '/') {
              window.location.href = '/self-assessment/dashboard';
            }
          }
        }
      }
    };

    checkOrg();
  }, [isAuthenticated, user, checkOrganizationRequirement]);

  // Migration: Phase 8.4 - Use package_tier-based logic instead of account_type
  // The shouldUseSelfAssessmentRoutes function is now imported from userAccessHelpers
  // For synchronous contexts, use shouldUseSelfAssessmentRoutesSync

  // Helper function to get the correct dashboard route based on user account type
  const getDashboardRoute = (userData) => {
    if (!userData) {
      return '/dashboard';
    }
    
    // If account_type is missing but user has organization_id and demo_expires_at, treat as demo
    if (!userData.account_type && userData.organization_id && userData.demo_expires_at) {
      return '/self-assessment/dashboard';
    }
    
    // Users with package_tier should go to self-assessment dashboard if they have organization
    if (shouldUseSelfAssessmentRoutesSync(userData)) {
      if (userData.organization_id) {
        return '/self-assessment/dashboard';
      }
      // Users with package_tier without organization go to onboarding
      return '/onboarding/organization';
    }
    
    // Regular users go to standard dashboard
    return '/dashboard';
  };

  const handleLogin = async (userData, token) => {
    setUser(userData);
    setIsAuthenticated(true);
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    
    // Check if demo user needs organization
    const needsOrg = await checkOrganizationRequirement(userData);
    if (needsOrg) {
      setNeedsOrganization(true);
      // Navigation will be handled by useEffect
    } else {
      // Immediately redirect to correct dashboard
      const dashboardRoute = getDashboardRoute(userData);
      if (dashboardRoute !== '/onboarding/organization') {
        // Use setTimeout to ensure state is set before navigation
        setTimeout(() => {
          window.location.href = dashboardRoute;
        }, 100);
      }
    }
  };

  const handleLogout = () => {
    setUser(null);
    setIsAuthenticated(false);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  };

  // Function to refresh user data from backend
  const refreshUser = async () => {
    try {
      const freshUser = await authService.getCurrentUser();
      if (freshUser) {
        setUser(freshUser);
        localStorage.setItem('user', JSON.stringify(freshUser));
        return freshUser;
      }
    } catch (error) {
      console.error('Error refreshing user:', error);
    }
    return null;
  };

  // Protected Route component that checks if demo user has organization
  // Defined inside App to have access to Router context
  const ProtectedRoute = ({ children }) => {
    const [checking, setChecking] = useState(true);
    const [needsOrg, setNeedsOrg] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();

    useEffect(() => {
      const checkOrg = async () => {
        if (!user) {
          setChecking(false);
          return;
        }

        // Skip check if already on wizard page
        if (location.pathname === '/onboarding/organization') {
          setChecking(false);
          return;
        }

        // Check if demo user needs organization
        const needs = await checkOrganizationRequirement(user);
        setNeedsOrg(needs);
        setChecking(false);

        if (needs) {
          navigate('/onboarding/organization', { replace: true });
        } else {
          // Check if user has package_tier and redirect if needed
          const canAccess = await shouldUseSelfAssessmentRoutes(user);
          if (canAccess && user.organization_id) {
            // User with package_tier should NOT be on /dashboard
            if (location.pathname === '/dashboard') {
              navigate('/self-assessment/dashboard', { replace: true });
              return;
            }
          }
        }
      };

      checkOrg();
    }, [user, location.pathname, navigate]);

    if (checking) {
      return (
        <div className="loading-container">
          <div className="spinner"></div>
        </div>
      );
    }

    if (needsOrg) {
      return null; // Navigation will happen
    }

    return children;
  };

  /** Route wrapper — extracts URL params and passes as props */
  const WithAssessmentId = ({ Component, user: u, onLogout: ol }) => {
    const { assessmentId } = useParams();
    return (
      <ProtectedRoute>
        <MasterTemplate user={u} onLogout={ol}>
          <Component assessmentId={assessmentId} />
        </MasterTemplate>
      </ProtectedRoute>
    );
  };

  const WithFrameworkId = ({ Component, user: u, onLogout: ol }) => {
    const { frameworkId } = useParams();
    return (
      <ProtectedRoute>
        <MasterTemplate user={u} onLogout={ol}>
          <Component frameworkId={frameworkId} />
        </MasterTemplate>
      </ProtectedRoute>
    );
  };

  return (
    <LogoProvider>
      <PermissionProvider user={user}>
      <Router
        future={{
          v7_startTransition: true,
          v7_relativeSplatPath: true
        }}
      >
        <div className="App">
          <a href="#main-content" className="skip-to-content">{t('a11y.skipToContent', 'Skip to content')}</a>
          <MessageBoxProvider>
          <ToastProvider>
        <ScrollToTop />
        {(loading || checkingOrganization) && (
          <div className="loading-container" style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 9999, background: 'rgba(255,255,255,0.9)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div className="spinner"></div>
          </div>
        )}
        <React.Suspense fallback={<SuspenseFallback />}>
        <Routes>
          {/* Home Page Route - MUST be first, shows for unauthenticated users */}
          <Route 
            path="/" 
            element={
              <RootRoute 
                isAuthenticated={isAuthenticated}
                user={user}
                loading={loading || checkingOrganization}
                getDashboardRoute={getDashboardRoute}
              />
            } 
          />

          {/* Public Survey Response Route - No Authentication Required */}
          <Route 
            path="/survey/respond/:token" 
            element={<SurveyResponsePage />} 
          />
          <Route 
            path="/interview/respond/:token" 
            element={<InterviewResponsePage />} 
          />

          {/* Authentication Routes - Always Available */}
          <Route 
            path="/signin" 
            element={
              isAuthenticated ? (
                <Navigate to={getDashboardRoute(user)} replace />
              ) : (
                <MasterTemplate layoutType="auth">
                  <SignIn onLogin={handleLogin} />
                </MasterTemplate>
              )
            } 
          />
          <Route 
            path="/signup" 
            element={
              isAuthenticated ? (
                <Navigate to={getDashboardRoute(user)} replace />
              ) : (
                <MasterTemplate layoutType="auth">
                  <SignUp onLogin={handleLogin} />
                </MasterTemplate>
              )
            } 
          />

          {/* Demo Signup Route - Part of Phase 1: Demo System Foundation */}
          <Route 
            path="/self-assessment/signup" 
            element={
              isAuthenticated ? (
                <Navigate to="/dashboard" replace />
              ) : (
                <SelfAssessmentSignUp onLogin={handleLogin} />
              )
            } 
          />

          {/* Email Verification Route - Part of Email Verification Before Login Implementation */}
          <Route 
            path="/verify-email/:token?" 
            element={
              isAuthenticated ? (
                <Navigate to={getDashboardRoute(user)} replace />
              ) : (
                <EmailVerification />
              )
            } 
          />
          
          {/* Invitation Acceptance Route - Public route for accepting team invitations */}
          <Route
            path="/invite/accept"
            element={<InviteAcceptPage />}
          />

          {/* Accreditation Public Routes (no auth) */}
          <Route
            path="/verify/:token"
            element={
              <React.Suspense fallback={<SuspenseFallback />}>
                <PublicVerification />
              </React.Suspense>
            }
          />
          <Route
            path="/registry"
            element={
              <React.Suspense fallback={<SuspenseFallback />}>
                <PublicRegistry />
              </React.Suspense>
            }
          />

          {/* Organization Wizard Route - Part of Phase 2: First-time Demo User Flow */}
          {/* This route is always accessible for authenticated users - no protection needed */}
          {isAuthenticated && (
            <Route 
              path="/onboarding/organization" 
              element={<SelfAssessmentOrganizationWizard onUserUpdate={setUser} />} 
            />
          )}

              {/* Demo Dashboard Route - Part of Phase 3: Demo Dashboard */}
              {/* Protected: demo users need organization to access */}
          {isAuthenticated && (
            <Route 
              path="/self-assessment/dashboard" 
              element={
                <SelfAssessmentRoute>
                <ProtectedRoute>
                  <SelfAssessmentDashboard />
                </ProtectedRoute>
                </SelfAssessmentRoute>
              } 
            />
          )}

          {/* Legacy redirect: /demo/maturity-report/:id -> /self-assessment/maturity-report/:id */}
          {isAuthenticated && (
            <Route path="/demo/maturity-report/:assessmentId" element={<DemoMaturityReportRedirect />} />
          )}

          {/* Demo Self Assessment Report Route */}
          {isAuthenticated && (
            <Route 
              path="/self-assessment/maturity-report/:assessmentId" 
              element={
                <SelfAssessmentRoute>
                <ProtectedRoute>
                  <SelfAssessmentPageLayout user={user} onLogout={handleLogout}>
                    <SelfAssessmentReport />
                  </SelfAssessmentPageLayout>
                </ProtectedRoute>
                </SelfAssessmentRoute>
              } 
            />
          )}

          {/* Self Assessment Details Route - Overview Only */}
          {isAuthenticated && (
            <Route 
              path="/self-assessment/assessment/:id" 
              element={
                <SelfAssessmentRoute>
                  <ProtectedRoute>
                    <SelfAssessmentPageLayout user={user} onLogout={handleLogout}>
                      <AssessmentDetailsPage isSelfAssessment={true} />
                    </SelfAssessmentPageLayout>
                  </ProtectedRoute>
                </SelfAssessmentRoute>
              } 
            />
          )}

          {/* Catch-all for unauthenticated users - redirect unknown routes to home (HomePage) */}
          {!isAuthenticated && !loading && (
            <Route 
              path="*" 
              element={<Navigate to="/" replace />} 
            />
          )}

          {/* Authenticated Routes - With Master Template */}
          {isAuthenticated && (
            <>
              {/* Assessment Runner - Conditional Layout based on account type */}
              {/* Protected: users with package_tier need organization to access */}
            <Route 
              path="/assessment/run/:assessmentId/*" 
              element={
                  <ProtectedRoute>
                    {shouldUseSelfAssessmentRoutesSync(user) ? (
                      <SelfAssessmentLayout user={user} onLogout={handleLogout}>
                        <AssessmentRunner isSelfAssessment={true} />
                      </SelfAssessmentLayout>
                    ) : (
                      <MasterTemplate user={user} onLogout={handleLogout}>
                        <AssessmentRunner isDemo={false} />
                      </MasterTemplate>
                    )}
                  </ProtectedRoute>
                } 
              />

              {/* Regular Pages - Full Layout */}
              {/* Protected: demo users need organization to access */}
              {/* BLOCK demo users from accessing /dashboard - redirect immediately */}
              <Route 
                path="/dashboard" 
                element={
                  <FullAccountRoute>
                  <ProtectedRoute>
                        <MasterTemplate user={user} onLogout={handleLogout}>
                          <Dashboard user={user} />
                        </MasterTemplate>
                  </ProtectedRoute>
                  </FullAccountRoute>
                } 
              />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <ProfilePage user={user} onUserUpdate={setUser} />
                    </MasterTemplate>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/location-tracking" 
                element={
                  <ProtectedRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <LocationDashboard />
                    </MasterTemplate>
                  </ProtectedRoute>
                } 
              />
              <Route
                path="/admin/backup"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <BackupPage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/restore"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <RestorePage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <UserManagement onUserRefresh={refreshUser} currentUser={user} />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/permissions"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <PermissionsManagement />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/organizations"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <OrganizationManagement />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route 
                path="/framework/builder" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <FrameworkBuilderWizard />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/framework/builder/:step" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <FrameworkBuilderWizard />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/frameworks/manage" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <FrameworkManagementPage />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/framework/:code" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <FrameworkStructureView />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/frameworks/:code" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <FrameworkDetailsPage />
                  </MasterTemplate>
                } 
              />
              {/* Redirect legacy /frameworks/:code/details to /frameworks/:code */}
              <Route
                path="/frameworks/:code/details"
                element={<FrameworkDetailsRedirect />}
              />
              <Route
                path="/framework/builder/edit/:code"
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <FrameworkBuilderWizard editMode={true} />
                  </MasterTemplate>
                }
              />
              {/* Redirect legacy /frameworks/builder/edit/:code to /framework/builder/edit/:code */}
              <Route
                path="/frameworks/builder/edit/:code"
                element={<FrameworkBuilderEditRedirect />}
              />
              <Route 
                path="/rtl-test" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <RTLTest />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/create-assessment" 
                element={
                  (() => {
                    const currentUser = (() => {
                      try {
                        const stored = localStorage.getItem('user');
                        return stored ? JSON.parse(stored) : null;
                      } catch {
                        return null;
                      }
                    })();
                    
                    // Use sync version for route guard (synchronous context)
                    if (shouldUseSelfAssessmentRoutesSync(currentUser) && currentUser?.organization_id) {
                      return <Navigate to="/self-assessment/dashboard" replace />;
                    }
                    
                    return (
                      <MasterTemplate user={user} onLogout={handleLogout}>
                        <AssessmentCreation />
                      </MasterTemplate>
                    );
                  })()
                } 
              />
              <Route 
                path="/assessment/:id" 
                element={
                  <FullAccountRoute>
                    <ProtectedRoute>
                      <MasterTemplate user={user} onLogout={handleLogout}>
                        <AssessmentDetailsPage />
                      </MasterTemplate>
                    </ProtectedRoute>
                  </FullAccountRoute>
                } 
              />
              <Route 
                path="/assessment/:assessmentId/survey/:surveyId" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <SurveyDetailsPage />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/assessment/:assessmentId/survey/:surveyId/responses" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <SurveyResponsesPage />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/assessment/:assessmentId/survey/:surveyId/responses/:recipientId" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <SurveyResponseDetailPage />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/assessment/:assessmentId/interview/:interviewId" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <InterviewDetailsPage />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/assessment-session/:sessionId" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <AssessmentSession />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/recommendations/:assessmentId" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <RecommendationsManagement />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/assessment-reports/:assessmentId" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <AssessmentReports />
                  </MasterTemplate>
                } 
              />
              <Route
                path="/maturity-report/:assessmentId"
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <MaturityReport />
                  </MasterTemplate>
                }
              />
              <Route
                path="/improvement-roadmap/:assessmentId"
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <ImprovementRoadmap />
                  </MasterTemplate>
                }
              />
              <Route
                path="/administration-dashboard"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <AdministrationDashboard />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route 
                path="/settings" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <SettingsPage />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/help" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <HelpPage />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/manual" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <ManualPage />
                  </MasterTemplate>
                } 
              />
              <Route 
                path="/documentation" 
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <DocumentationPage />
                  </MasterTemplate>
                } 
              />
              {/* Self Assessment Admin Routes */}
              {/* Redirects for old /admin/demo/* paths */}
              <Route 
                path="/admin/demo/users" 
                element={<Navigate to="/admin/self-assessment/users" replace />} 
              />
              <Route 
                path="/admin/demo/organizations" 
                element={<Navigate to="/admin/self-assessment/organizations" replace />} 
              />
              <Route 
                path="/admin/demo/activity" 
                element={<Navigate to="/admin/self-assessment/activity" replace />} 
              />
              <Route 
                path="/admin/demo/rankings" 
                element={<Navigate to="/admin/self-assessment/rankings" replace />} 
              />
              <Route 
                path="/admin/demo/config" 
                element={<Navigate to="/admin/self-assessment/config" replace />} 
              />
              <Route
                path="/admin/self-assessment/users"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <SelfAssessmentUsersManagement />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/self-assessment/activity"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <SelfAssessmentActivityPage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/self-assessment/lead-scores"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <LeadScoringPage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/demo/lead-scores"
                element={<Navigate to="/admin/self-assessment/lead-scores" replace />}
              />
              <Route
                path="/admin/self-assessment/organizations"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <SelfAssessmentOrganizationsPage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/self-assessment/rankings"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <SelfAssessmentRankingsPage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/self-assessment/email-campaigns"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <EmailCampaignsPage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/demo/email-campaigns"
                element={<Navigate to="/admin/self-assessment/email-campaigns" replace />}
              />
              <Route
                path="/admin/self-assessment/config"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <SelfAssessmentConfigPage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/org-groups"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <OrgGroupsPage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/payment-methods"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <PaymentMethodsPage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route 
                path="/upgrade" 
                element={
                  (() => {
                    // Check if user is demo account - use demo layout
                    const currentUser = user || (() => {
                      try {
                        const stored = localStorage.getItem('user');
                        return stored ? JSON.parse(stored) : null;
                      } catch {
                        return null;
                      }
                    })();
                    
                    // Use sync version for route guard (synchronous context)
                    // Migration: Phase 8.4 - Removed account_type check, use package_tier only
                    const shouldUseSelfAssessment = shouldUseSelfAssessmentRoutesSync(currentUser);
                    
                    if (shouldUseSelfAssessment) {
                      return (
                        <SelfAssessmentRoute>
                          <ProtectedRoute>
                            <SelfAssessmentPageLayout user={user} onLogout={handleLogout}>
                              <UpgradePage />
                            </SelfAssessmentPageLayout>
                          </ProtectedRoute>
                        </SelfAssessmentRoute>
                      );
                    }
                    
                    // Full account - use full layout
                    return (
                      <FullAccountRoute>
                        <ProtectedRoute>
                          <MasterTemplate user={user} onLogout={handleLogout}>
                            <UpgradePage />
                          </MasterTemplate>
                        </ProtectedRoute>
                      </FullAccountRoute>
                    );
                  })()
                } 
              />
              {/* Groot chat is now a floating widget in MasterTemplate — redirect old route */}
              <Route
                path="/ai-chat"
                element={<Navigate to="/dashboard" replace />}
              />
              <Route
                path="/admin/ai-agent-config"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <AIAgentConfigPage />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/standards-demo"
                element={
                  <MasterTemplate user={user} onLogout={handleLogout}>
                    <StandardsSelectionDemo />
                  </MasterTemplate>
                }
              />
              {/* Phase 2: Enterprise Assessment Features */}
              <Route path="/frameworks/:frameworkId/weights" element={<WithFrameworkId Component={WeightConfiguration} user={user} onLogout={handleLogout} />} />
              <Route path="/assessments/:assessmentId/targets" element={<WithAssessmentId Component={TargetSetting} user={user} onLogout={handleLogout} />} />
              <Route path="/assessments/:assessmentId/cycles" element={<WithAssessmentId Component={AssessmentCycles} user={user} onLogout={handleLogout} />} />
              <Route path="/assessments/:assessmentId/assessors" element={<WithAssessmentId Component={AssessorAssignment} user={user} onLogout={handleLogout} />} />
              <Route path="/assessments/:assessmentId/consensus" element={<WithAssessmentId Component={ConsensusView} user={user} onLogout={handleLogout} />} />
              {/* Phase 3: Certification Features */}
              <Route path="/assessments/:assessmentId/evidence" element={<WithAssessmentId Component={EvidenceSufficiency} user={user} onLogout={handleLogout} />} />
              <Route path="/assessments/:assessmentId/workflow" element={<WithAssessmentId Component={WorkflowActions} user={user} onLogout={handleLogout} />} />
              <Route
                path="/admin/audit-log"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <AuditLog />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              {/* Accreditation Admin Routes */}
              <Route
                path="/admin/accreditation"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <React.Suspense fallback={<SuspenseFallback />}>
                        <AccreditationDashboard />
                      </React.Suspense>
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/applications"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <React.Suspense fallback={<SuspenseFallback />}>
                        <AccreditationApplications />
                      </React.Suspense>
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/applications/:id"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <React.Suspense fallback={<SuspenseFallback />}>
                        <AccreditationApplicationDetail />
                      </React.Suspense>
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/criteria"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <React.Suspense fallback={<SuspenseFallback />}>
                        <AccreditationCriteria />
                      </React.Suspense>
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/certificates"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <React.Suspense fallback={<SuspenseFallback />}>
                        <CertificateManagement />
                      </React.Suspense>
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/profile/:orgId"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <React.Suspense fallback={<SuspenseFallback />}>
                        <AccreditationProfile />
                      </React.Suspense>
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/settings"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <React.Suspense fallback={<SuspenseFallback />}>
                        <AccreditationSettings />
                      </React.Suspense>
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/analytics"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <AccreditationAnalytics />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/audit"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <AuditTrailViewer />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/:id/renewal"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <AccreditationRenewal />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/compliance"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <ComplianceReport />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/accreditation/benchmarks"
                element={
                  <AdminRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <BenchmarkDashboard />
                    </MasterTemplate>
                  </AdminRoute>
                }
              />
              {/* Phase 4: Billing & Pricing */}
              <Route
                path="/pricing"
                element={
                  <React.Suspense fallback={<SuspenseFallback />}>
                    <PricingPage />
                  </React.Suspense>
                }
              />
              <Route
                path="/subscription"
                element={
                  <ProtectedRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <SubscriptionManagement />
                    </MasterTemplate>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/invoices"
                element={
                  <ProtectedRoute>
                    <MasterTemplate user={user} onLogout={handleLogout}>
                      <InvoiceHistory />
                    </MasterTemplate>
                  </ProtectedRoute>
                }
              />
            </>
          )}

          {/* Catch-all for authenticated users: redirect unknown routes to dashboard */}
          {isAuthenticated && (
            <Route
              path="*"
              element={<Navigate to={getDashboardRoute(user)} replace />}
            />
          )}
        </Routes>
        </React.Suspense>
        </ToastProvider>
          </MessageBoxProvider>
        </div>
      </Router>
      </PermissionProvider>
    </LogoProvider>
  );
}

export default App;
